import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { currentSchool } from './school';
import type { IntegrationBase, PersonSummary, PersonType } from './types';

// ---------------------------------------------------------------------------
// The student information systems Edvora rosters from
// ---------------------------------------------------------------------------

export type SisVendor = 'POWERSCHOOL' | 'GENESIS' | 'INFINITE_CAMPUS' | 'SKYWARD' | 'CLASSLINK' | 'VERACROSS' | 'OTHER';
export type OneRosterVersion = '1.1' | '1.2';
export type OneRosterAuth = 'OAUTH2' | 'OAUTH1';
export type OneRosterRole = 'student' | 'teacher' | 'administrator' | 'aide' | 'proctor' | 'guardian' | 'parent' | 'relative';

export interface VendorGuide {
  value: SisVendor;
  label: string;
  version: OneRosterVersion;
  auth: OneRosterAuth;
  /** What the API address looks like. */
  addressHint: string;
  /** The token URL when it has to be typed in. */
  tokenHint?: string;
  scope?: string;
  steps: string[];
}

export const VENDORS: VendorGuide[] = [
  {
    value: 'POWERSCHOOL',
    label: 'PowerSchool',
    version: '1.1',
    auth: 'OAUTH2',
    addressHint: 'https://yourdistrict.powerschool.com',
    steps: [
      'In PowerSchool SIS, go to System › System Settings › Plugin Management Configuration and install and turn on the OneRoster plugin.',
      'Open the plugin’s data configuration and copy its client ID and client secret.',
      'Enter your PowerSchool server’s address. Edvora adds /ims/oneroster/v1p1, and gets tokens from /oauth/access_token.',
    ],
  },
  {
    value: 'GENESIS',
    label: 'Genesis',
    version: '1.1',
    auth: 'OAUTH2',
    addressHint: 'https://yourdistrict.genesisedu.net/genesis',
    steps: [
      'Ask your Genesis customer relationship manager to turn on the OneRoster API for Edvora.',
      'Genesis sends an API address (your Genesis site, ending in /genesis), a client ID, and a client secret.',
      'Enter the API address as Genesis sent it. Edvora gets tokens from /token under it.',
    ],
  },
  {
    value: 'INFINITE_CAMPUS',
    label: 'Infinite Campus',
    version: '1.2',
    auth: 'OAUTH2',
    addressHint: 'https://yourdistrict.infinitecampus.org/campus/api',
    tokenHint: 'https://yourdistrict.infinitecampus.org/campus/oauth2/token?appName=…',
    steps: [
      'In Infinite Campus, set up a OneRoster connection for Edvora with OAuth 2.0 turned on.',
      'Copy the OneRoster base URL, the client ID, the client secret, and the token URL Campus shows with them.',
      'Choose OneRoster 1.2 if the base URL ends in /rostering/v1p2, or 1.1 for /v1p1.',
    ],
  },
  {
    value: 'SKYWARD',
    label: 'Skyward',
    version: '1.1',
    auth: 'OAUTH2',
    addressHint: 'The OneRoster API URL from Skyward',
    tokenHint: 'The access token URL from Skyward',
    steps: [
      'In Skyward (SMS 2.0 or Qmlativ), open the OneRoster API configuration (in Qmlativ, Administrative Access › API › One Roster Configuration) and add Edvora.',
      'Copy the OneRoster API URL, the key and secret, and the access token URL. The token URL is different from the web address you sign in at.',
    ],
  },
  {
    value: 'CLASSLINK',
    label: 'ClassLink',
    version: '1.1',
    auth: 'OAUTH1',
    addressHint: 'https://yourdistrict-xx-v2.oneroster.com/ims/oneroster/',
    steps: [
      'In ClassLink Roster Server, add a OneRoster app for Edvora and choose what it can see.',
      'Copy the OneRoster endpoint, the client ID, and the client secret from the app’s settings.',
      'ClassLink signs each request with OAuth 1.0a (HMAC-SHA256), so there’s no token URL.',
    ],
  },
  {
    value: 'VERACROSS',
    label: 'Veracross',
    version: '1.1',
    auth: 'OAUTH2',
    addressHint: 'https://oneroster.veracross.com/yourschool/ims/oneroster/v1p1',
    scope: 'https://purl.imsglobal.org/spec/or/v1p1/scope/roster.readonly',
    steps: [
      'Veracross’s OneRoster API comes with API Plus for Academics.',
      'In Veracross, create an OAuth application for Edvora with the OneRoster roster scope, and copy its client ID and secret.',
      'Enter https://oneroster.veracross.com/ followed by your school’s route. Edvora gets tokens from accounts.veracross.com.',
    ],
  },
  {
    value: 'OTHER',
    label: 'Another OneRoster SIS',
    version: '1.1',
    auth: 'OAUTH2',
    addressHint: 'https://sis.example.org/ims/oneroster/v1p1',
    tokenHint: 'https://sis.example.org/oauth/token',
    steps: ['Any SIS certified for OneRoster 1.1 or 1.2 rostering works: you need its OneRoster API address, a client ID and secret, and, for OAuth 2, its token URL.'],
  },
];

export const vendorGuide = (vendor: SisVendor) => VENDORS.find((entry) => entry.value === vendor) ?? VENDORS[VENDORS.length - 1];

export const ROLE_LABELS: Record<OneRosterRole, string> = {
  student: 'Students',
  teacher: 'Teachers',
  administrator: 'Administrators and other staff',
  aide: 'Aides',
  proctor: 'Proctors',
  guardian: 'Guardians',
  parent: 'Parents',
  relative: 'Relatives',
};

// ---------------------------------------------------------------------------
// The integration and what it synced
// ---------------------------------------------------------------------------

export interface OneRosterSettings {
  vendor: SisVendor;
  source: 'API' | 'CSV';
  baseUrl?: string;
  version: OneRosterVersion;
  authMethod: OneRosterAuth;
  tokenUrl?: string;
  scope?: string;
  pageSize: number;
  csvUrl?: string;
  roleTypes: Record<OneRosterRole, PersonType | null>;
  schoolIds: string[];
  syncClasses: boolean;
  deactivateMissing: boolean;
  departureSafetyPercent: number;
  matchExisting: boolean;
  fieldMappings: { key: string; fieldId: string }[];
}

export interface OneRosterIntegration extends IntegrationBase {
  provider: 'ONEROSTER';
  settings: OneRosterSettings;
  oneRoster: {
    clientId: string | null;
    hasClientSecret: boolean;
    csvUsername: string | null;
    hasCsvPassword: boolean;
    apiUrl: string | null;
    defaultTokenUrl: string | null;
    defaultScope: string | null;
    secretsUnreadable: boolean;
  };
}

export interface RosterUpload {
  id: string;
  fileName: string;
  format: 'ONEROSTER_ZIP' | 'CSV';
  summary: string | null;
  sizeBytes: number;
  uploadedByName: string;
  createdAt: string;
}

export interface RosterOverview {
  schoolYear: string | null;
  people: { active: Partial<Record<PersonType, number>>; inactive: number };
  schools: number;
  classes: number;
  enrollments: number;
  transfersLast30Days: number;
  metadataKeys: { key: string; sample: string; count: number }[];
  uploads: RosterUpload[];
  latestUploadId: string | null;
  rollup: { id: string; toYear: string; holdUntil: string } | null;
}

export interface RosterSchool {
  id: string;
  sourcedId: string;
  name: string;
  identifier: string | null;
  active: boolean;
  lastSyncedAt: string | null;
  building: { id: string; name: string } | null;
  students: number;
  staff: number;
  classes: number;
}

type PersonName = Pick<PersonSummary, 'id' | 'firstName' | 'lastName' | 'preferredName'>;

export interface RosterClassItem {
  id: string;
  title: string;
  classCode: string | null;
  classType: string | null;
  location: string | null;
  periods: string[];
  grades: string[];
  subjects: string[];
  schoolYear: string | null;
  active: boolean;
  school: { id: string; name: string } | null;
  teachers: PersonName[];
  studentCount: number;
  teacherCount: number;
}

export interface RosterClassDetail extends Omit<RosterClassItem, 'teachers' | 'studentCount' | 'teacherCount'> {
  sourcedId: string;
  lastSyncedAt: string;
  enrollments: { id: string; role: string; primary: boolean; person: PersonSummary }[];
}

export interface RosterTransfer {
  id: string;
  fromSchoolName: string;
  toSchoolName: string;
  detectedAt: string;
  person: PersonSummary & { activeCheckoutCount: number };
}

export interface RosterRollup {
  id: string;
  fromYear: string;
  toYear: string;
  graduatingGrade: string;
  promoted: number;
  graduated: number;
  classesArchived: number;
  holdUntil: string;
  finishedAt: string | null;
  finishReason: string | null;
  createdByName: string;
  createdAt: string;
}

export interface RollupOverview {
  schoolYear: string | null;
  grades: { grade: string | null; count: number }[];
  classYears: { schoolYear: string | null; count: number }[];
  active: RosterRollup | null;
  history: RosterRollup[];
  suggested: { fromYear: string; toYear: string; graduatingGrade: string; holdUntil: string; archiveClasses: boolean };
}

export const useRosterOverview = (enabled = true) =>
  useQuery({ queryKey: ['oneroster', 'overview'], queryFn: () => api.get<RosterOverview>('/oneroster/overview'), enabled });

export const useRosterSchools = (enabled = true) =>
  useQuery({ queryKey: ['oneroster', 'schools'], queryFn: () => api.get<RosterSchool[]>('/oneroster/schools'), enabled, staleTime: 30_000 });

// ---------------------------------------------------------------------------
// Grades and school years
// ---------------------------------------------------------------------------

export const GRADE_ORDER = ['IT', 'PR', 'PK', 'TK', 'KG', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', 'PS'] as const;

const GRADE_NAMES: Record<string, string> = {
  IT: 'Infant/toddler',
  PR: 'Preschool',
  PK: 'Pre-K',
  TK: 'Transitional K',
  KG: 'Kindergarten',
  PS: 'Postsecondary',
  UG: 'Ungraded',
};

/** "09" → "Grade 9", "KG" → "Kindergarten". Grades named some other way show as they are. */
export function gradeLabel(grade: string | null | undefined) {
  if (!grade) return '';
  if (GRADE_NAMES[grade]) return GRADE_NAMES[grade];
  return /^\d{2}$/.test(grade) ? `Grade ${Number(grade)}` : grade;
}

/** "2027" → "2026–27". */
export function schoolYearLabel(year: string | null | undefined) {
  if (!year || !/^\d{4}$/.test(year)) return year ?? '';
  return `${Number(year) - 1}–${year.slice(2)}`;
}

// ---------------------------------------------------------------------------
// Uploads
// ---------------------------------------------------------------------------

/** Uploads a roster file, reporting progress, since district exports can be large. */
export function uploadRosterFile(file: File, onProgress: (fraction: number) => void) {
  return new Promise<{ upload: RosterUpload; syncQueued: boolean }>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', '/api/oneroster/uploads');
    request.withCredentials = true;
    request.setRequestHeader('content-type', 'application/octet-stream');
    request.setRequestHeader('x-file-name', encodeURIComponent(file.name));
    if (currentSchool) request.setRequestHeader('x-edvora-school', currentSchool);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    request.onload = () => {
      let body: { error?: string } & Record<string, unknown> = {};
      try {
        body = JSON.parse(request.responseText || '{}');
      } catch {
        // Not JSON.
      }
      if (request.status >= 200 && request.status < 300) resolve(body as unknown as { upload: RosterUpload; syncQueued: boolean });
      else reject(new Error(body.error ?? `The upload failed (HTTP ${request.status})`));
    };
    request.onerror = () => reject(new Error('The upload didn’t finish. Check your connection and try again.'));
    request.send(file);
  });
}
